import { useState, useRef, useEffect } from 'react';

const useCamera = () => {
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [currentSide, setCurrentSide] = useState('front');
  const [credentialImages, setCredentialImages] = useState({ front: null, back: null });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Clean up camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = () => {
    setShowCamera(true);
    
    // Determine which side to capture based on what we already have
    if (!credentialImages.front) {
      setCurrentSide('front');
    } else if (!credentialImages.back) {
      setCurrentSide('back');
    }
    
    // Start camera
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then(mediaStream => {
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play();
        }
      })
      .catch(err => {
        console.error("Error accessing camera:", err);
        alert("Unable to access camera. Please ensure you have granted camera permissions.");
        setShowCamera(false);
      });
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Get the image as data URL
      const imageDataUrl = canvas.toDataURL('image/png');
      
      // Update the appropriate side of the credential
      setCredentialImages(prev => ({
        ...prev,
        [currentSide]: imageDataUrl
      }));
      
      // Stop camera after capturing
      stopCamera();
      
      return true;
    }
    return false;
  };

  const retakePhoto = (side) => {
    // Set the current side to retake
    setCurrentSide(side);
    
    // Start the camera to retake the photo
    startCamera();
  };

  const flipCamera = () => {
    if (stream) {
      // Stop current stream
      stream.getTracks().forEach(track => track.stop());
      
      // Toggle facing mode
      const currentFacingMode = stream.getVideoTracks()[0].getSettings().facingMode;
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      
      // Start new stream with flipped camera
      navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: newFacingMode }, 
        audio: false 
      })
      .then(newStream => {
        setStream(newStream);
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          videoRef.current.play();
        }
      });
    }
  };

  return {
    stream,
    showCamera,
    currentSide,
    credentialImages,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    captureImage,
    flipCamera,
    retakePhoto,
    setCredentialImages
  };
};

export default useCamera;